import { useRef, useState, type ReactNode } from "react"
import { Loader2 } from "lucide-react"
import { haptic } from "@/lib/haptics"

interface PullToRefreshProps {
  onRefresh: () => Promise<void>
  children: ReactNode
  className?: string
}

const PULL_THRESHOLD = 64 // px before triggering refresh

/**
 * Wraps a scrollable list with pull-to-refresh behavior.
 * Works on both web and native (haptic feedback fires on native only).
 */
export function PullToRefresh({ onRefresh, children, className = "" }: PullToRefreshProps) {
  const [pullDistance, setPullDistance] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const startY = useRef<number | null>(null)
  const triggered = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)

  function onTouchStart(e: React.TouchEvent) {
    const scrollTop = containerRef.current?.scrollTop ?? 0
    if (scrollTop > 0) return
    startY.current = e.touches[0].clientY
    triggered.current = false
  }

  function onTouchMove(e: React.TouchEvent) {
    if (startY.current === null || refreshing) return
    const scrollTop = containerRef.current?.scrollTop ?? 0
    if (scrollTop > 0) {
      startY.current = null
      return
    }
    const dist = Math.max(0, e.touches[0].clientY - startY.current)
    // Apply rubber-band damping
    const damped = Math.min(dist * 0.4, PULL_THRESHOLD * 1.5)
    setPullDistance(damped)

    // Fire haptic at threshold
    if (damped >= PULL_THRESHOLD && !triggered.current) {
      triggered.current = true
      haptic.medium()
    }
  }

  async function onTouchEnd() {
    if (startY.current === null) return
    startY.current = null

    if (pullDistance >= PULL_THRESHOLD && !refreshing) {
      setRefreshing(true)
      setPullDistance(0)
      haptic.success()
      try {
        await onRefresh()
      } finally {
        setRefreshing(false)
        triggered.current = false
      }
    } else {
      setPullDistance(0)
      triggered.current = false
    }
  }

  return (
    <div
      ref={containerRef}
      className={`relative overflow-y-auto ${className}`}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Pull indicator */}
      {(pullDistance > 0 || refreshing) && (
        <div
          className="absolute top-0 left-0 right-0 flex items-center justify-center transition-all duration-150 pointer-events-none z-10"
          style={{ height: refreshing ? 48 : pullDistance }}
        >
          <Loader2
            className="h-5 w-5 text-primary animate-spin"
            style={{ opacity: refreshing ? 1 : Math.min(pullDistance / PULL_THRESHOLD, 1) }}
          />
        </div>
      )}
      <div style={{ transform: refreshing ? "translateY(48px)" : `translateY(${pullDistance}px)`, transition: refreshing ? "transform 0.2s ease" : undefined }}>
        {children}
      </div>
    </div>
  )
}
